import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AnonymousGuestAuthBodyDto,
  EmailGuestAuthBodyDto,
  GalleryDto,
  GuestAuthBodyDto,
  GuestUserDto,
  UserActivitiesNotificationSettingsDto,
  UserNotificationSettingsDto,
} from '@picflow/types';

import { TokenResult } from '@stripe/stripe-js';
import { BehaviorSubject, Observable, Subject, lastValueFrom } from 'rxjs';
import { AssetFeedback } from '../gallery-shared/interfaces/asset-feedback';
import { copy } from '../lib/copy';
import { getAllRouteData } from '../lib/get-all-route-data';
import { getAnonymousGuestName } from '../lib/get-anonymous-guest-name';
import { parseJwt } from '../lib/parse-jwt';
import { randomString } from '../lib/random-string';
import { AppConfigService } from '../providers/config.service';
import { GuestProfileDialogComponent } from '../view-gallery/dialogs/guest-profile-dialog/guest-profile-dialog.component';
import { UserFoundDialogComponent } from '../view-gallery/dialogs/user-found-dialog/user-found-dialog.component';
import { AnalyticsService } from './analytics.service';
import { BrowserService } from './browser.service';
import { DataLoaderService } from './data-loader.service';
import { DialogService } from './dialog.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { VisitorService } from './visitor.service';

@Injectable({
  providedIn: 'root',
})
export class GuestAuthService {
  private _auth: Subject<boolean> = new BehaviorSubject<boolean>(undefined);
  public readonly onAuthChange: Observable<boolean> = this._auth.asObservable();

  constructor(
    private http: HttpClient,
    private config: AppConfigService,
    private dialog: DialogService,
    private state: StateService,
    private analytics: AnalyticsService,
    private storage: StorageService,
    private route: ActivatedRoute,
    private browserService: BrowserService,
    private visitorService: VisitorService,
    private loader: DataLoaderService
  ) {
    this.init();
    this.state.renewGuestAuthToken = () => {
      return this.renewAuthIfNeeded();
    };
  }

  public async init() {
    if (this.storage.hasAny('guest-auth')) {
      const auth = this.storage.getAny('guest-auth');
      if (!auth) return;
      if (typeof auth === 'string') {
        this.state.guestAuthToken = auth; // new way
      }
      if (auth.email || auth.anonymous_id) {
        this.state.guestAuthToken = auth.token; // old way, but we need to keep it for a while
      }
    }
    if (this.state.guestAuthToken) {
      this.renewAuthIfNeeded();
    }
  }

  public checkAuthTokenValid(token: string) {
    let resp: TokenResult;
    try {
      resp = parseJwt(token);
    } catch (err) {
      console.log(err);
      return false;
    }
    return resp;
  }

  public checkAuthTokenExpired(token: string): boolean {
    const resp = parseJwt(token);
    const expires = resp.exp * 1000;
    const timeTillExpire = expires - Date.now();
    return timeTillExpire < 1000 * 60;
  }

  public async authenticateWithEmail(
    name: string,
    email: string,
    notify: boolean,
    gallery: GalleryDto,
    feedback?: AssetFeedback
  ) {
    if (name) name = name.trim();
    if (email) email = email.toLowerCase().trim();

    const notification_settings: Partial<UserNotificationSettingsDto> | undefined =
      notify === undefined
        ? undefined
        : {
            activities: <UserActivitiesNotificationSettingsDto>{
              annotations: notify,
              asset_statuses: notify,
              color_labels: notify,
              comments: notify,
              favourites: notify,
              gallery_downloads: notify,
              gallery_views: notify,
              archives: notify,
              mentions: notify,
            },
          };

    const params: EmailGuestAuthBodyDto = { name, email, notification_settings };
    return this.authenticate(params, gallery, feedback);
  }

  public prepAnonymousGuest() {
    if (this.state.preGuestAnonymousId) return;
    const anonymous_id = this.storage.get('preppedGuestAnonymousId') || randomString(36);
    this.state.preGuestAnonymousId = anonymous_id;
    this.storage.set('preppedGuestAnonymousId', anonymous_id);
  }

  public authenticateForFeedback(gallery: GalleryDto, feedback?: AssetFeedback) {
    // using loader here to prevent multiple calls to the backend for
    return this.loader.get(
      'authenticateForFeedback',
      () => {
        return this.authenticateAnonymously(gallery, undefined, feedback);
      },
      1000
    );
  }

  public async authenticateAnonymously(gallery: GalleryDto, name?: string, feedback?: AssetFeedback) {
    if (!this.state.preGuestAnonymousId) this.prepAnonymousGuest();
    const anonymous_id = this.state.preGuestAnonymousId;
    const params: AnonymousGuestAuthBodyDto = {
      anonymous_id,
      name: name || getAnonymousGuestName(anonymous_id),
    };
    return this.authenticate(params, gallery, feedback);
  }

  private async authenticate(params: GuestAuthBodyDto, gallery: GalleryDto, feedback?: AssetFeedback) {
    try {
      const resp = await lastValueFrom(
        this.http.post<GuestUserDto>(`${this.config.apiEndpoint}/auth/guest`, params, { observe: 'response' })
      );
      this.state.preGuestAnonymousId = undefined;
      this.storage.remove('preppedGuestAnonymousId');
      this.analytics.track('guest_identified');

      this.state.guest = resp.body;

      this.state.guestAuthToken = resp.headers.get('authorization')?.split('Bearer ')[1];
      if (this.state.guestAuthToken) {
        console.debug('Saved guest auth token');
      } else {
        console.debug('No guest auth token');
      }
      const auth = {
        token: this.state.guestAuthToken,
        id: this.state.guest['id'],
        email: this.state.guest.email,
        anonymous_id: this.state.guest.anonymous_id,
      };
      this.storage.setCookie('guest-auth', this.state.guestAuthToken);

      this.visitorService.createVisitor$.next(gallery.id);

      this._auth.next(true);
      return auth;
    } catch (err) {
      if (err.error.code === 'account_already_exists') {
        await this.accountLogin(params['email'], gallery, feedback);
        return;
      }
      this.analytics.track('guest_identification_failed');
      throw err;
    }
  }

  public async authenticateWithPopup(gallery: GalleryDto, feedback?: AssetFeedback) {
    await this.dialog.open(GuestProfileDialogComponent, { gallery, feedback, tenant: gallery?.['tenant'] });
    return !!this.state.guestAuthToken;
  }

  public logout() {
    this.storage.remove('guest-auth');
    this.storage.removeCookie('guest-auth');
    this.state.guestAuthToken = undefined;
    this.state.guest = undefined;
    console.debug('Logged out guest');
    this._auth.next(false);
    location.reload(); // reloading to clear is_liked from assets, etc.
  }

  private getAccountLoginRedirectUrlBase(gallery: GalleryDto) {
    if (this.browserService.getTenantCustomDomain()) {
      // going to subdomain
      const tenantPath = gallery['tenant']?.path;
      if (tenantPath) {
        const picflowUrlBase = `${this.config.env === 'production' ? '' : this.config.env + '.'}picflow.com`;
        return `https://${tenantPath}.${picflowUrlBase}${location.pathname}`;
      }
    }

    // already on subdomain, will come back here
    if (this.browserService.getTenantSubDomain()) return location.origin + location.pathname;

    // Fallback
    const data = getAllRouteData(this.route.snapshot);
    if (data.data?.asset) return `/gallery/${data.gallery.id}/${data.data.asset.id}`;
    return `/gallery/${data.gallery.id}`;
  }

  private async accountLogin(email: string, gallery: GalleryDto, feedback?: AssetFeedback) {
    const returnUrl = this.getAccountLoginRedirectUrlBase(gallery);

    const params = copy(this.route.snapshot.queryParams);
    if (feedback) params.feedback = JSON.stringify(feedback);
    // const code = this.storage.get(`gallery_${data.gallery.id}_code`);
    // params.code = code;
    let search = new URLSearchParams(params).toString();
    if (search) search = '?' + search;
    const resp = await this.dialog.open(UserFoundDialogComponent, {});
    if (!resp) return;
    location.href = `${this.browserService.getCorrectPicflowUrl()}/auth/login?email=${encodeURIComponent(
      email
    )}&returnUrl=${encodeURIComponent(returnUrl + search)}`;
  }

  private async renewAuthIfNeeded() {
    if (!this.state.guestAuthToken) return;
    if (!this.checkAuthTokenValid(this.state.guestAuthToken)) return this.logout();
    if (!this.checkAuthTokenExpired(this.state.guestAuthToken)) return; // no need to renew because it's not expired

    if (!this.state.guest) return;

    const name = 'Renewal';
    const email = this.state.guest.email;
    const anonymous_id = this.state.guest.anonymous_id;

    const params: GuestAuthBodyDto = { name, email, anonymous_id };
    try {
      const resp = await lastValueFrom(
        this.http.post<GuestUserDto>(`${this.config.apiEndpoint}/auth/guest`, params, { observe: 'response' })
      );

      this.state.guest = resp.body;

      this.state.guestAuthToken = resp.headers.get('authorization')?.split('Bearer ')[1];

      if (this.state.guestAuthToken) {
        console.debug('Renewed guest auth token from renewAuthIfNeeded');
      } else {
        console.debug('No renewed guest auth token from renewAuthIfNeeded');
      }
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.status !== 400) throw err;
      // Assuming this means: Account with email "..." already exists (they signed up for a picflow account)
      return this.logout();
    }
  }
}
